import { useState, useEffect, useRef } from 'react'
import { X, CreditCard, Lock, Loader2 } from 'lucide-react'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import { Charity } from './CharityCard'
import { paySkyService } from '@/services/paySkyService'
import { donationsApi } from '@/services/donationsApiService'
import { useTranslation } from 'react-i18next'

interface DonationModalProps {
  isOpen: boolean
  charity: Charity | null
  suggestedAmount?: number
  onClose: () => void
  onConfirm: (
    charity: Charity,
    amount: number,
    donorName?: string,
    donorEmail?: string
  ) => void
}

export default function DonationModal({
  isOpen,
  charity,
  suggestedAmount,
  onClose,
  onConfirm,
}: DonationModalProps) {
  const { t, i18n } = useTranslation()
  const [amount, setAmount] = useState<string>(suggestedAmount?.toString() || '')
  const [donorName, setDonorName] = useState('')
  const [donorEmail, setDonorEmail] = useState('')
  const [step, setStep] = useState<'details' | 'payment'>('details')
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const callbackProcessedRef = useRef(false) // Track if callback has been processed

  useEffect(() => {
    if (suggestedAmount) {
      setAmount(suggestedAmount.toString())
    }
  }, [suggestedAmount])

  useEffect(() => {
    if (!isOpen) {
      setStep('details')
      setAmount(suggestedAmount?.toString() || '')
      setDonorName('')
      setDonorEmail('')
      setPaymentError(null)
      setIsProcessing(false)
      callbackProcessedRef.current = false // Reset callback flag when modal closes
    }
  }, [isOpen, suggestedAmount])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen || !charity) return null

  const donationAmount = parseFloat(amount) || 0
  const isValid = donationAmount > 0 && donorName.trim() && donorEmail.trim()

  const handleNext = () => {
    if (isValid) {
      setStep('payment')
    }
  }

  const handleConfirm = async () => {
    if (!isValid || !charity) return

    // Reset callback flag for new payment
    callbackProcessedRef.current = false

    setIsProcessing(true)
    setPaymentError(null)

    try {
      // Step 1: Create donation in backend first
      const donationResponse = await donationsApi.createDonation({
        charityId: charity.id,
        amount: donationAmount,
        donorName: donorName.trim(),
        donorEmail: donorEmail.trim(),
        source: 'website', // Classify donation source
      })

      // Step 2: Use donation ID as merchant reference for PaySky
      const merchantReference = donationResponse.id

      await paySkyService.initializePayment({
        amount: donationAmount,
        merchantReference,
        onComplete: async (data) => {
          // Process only the first callback
          if (callbackProcessedRef.current) {
            return
          }

          callbackProcessedRef.current = true

          setIsProcessing(false)
          onClose()

          // Notify parent with success
          onConfirm(charity, donationAmount, donorName, donorEmail)
        },
        onError: (error) => {
          // Process only the first error callback
          if (callbackProcessedRef.current) {
            return
          }

          callbackProcessedRef.current = true

          setIsProcessing(false)

          // Extract error message - handle both string and object errors
          let errorMessage = t('payment.error', 'حدث خطأ أثناء معالجة الدفع. يرجى المحاولة مرة أخرى.')
          
          if (typeof error === 'string') {
            errorMessage = error
          } else if (error?.message) {
            errorMessage = error.message
          } else if (error?.response?.data?.error) {
            const errorData = error.response.data.error
            if (typeof errorData === 'string') {
              errorMessage = errorData
            } else if (errorData?.message) {
              errorMessage = errorData.message
            }
          }
          
          setPaymentError(errorMessage)
        },
      })

      // Step 3: After initialization, open PaySky Lightbox
      paySkyService.showPaymentLightbox()
    } catch (error: any) {
      setIsProcessing(false)
      
      // Extract error message - handle both string and object errors
      let errorMessage = t('payment.initError', 'فشل في تهيئة نظام الدفع. يرجى المحاولة مرة أخرى.')
      
      if (error?.response?.data?.error) {
        const errorData = error.response.data.error
        if (typeof errorData === 'string') {
          errorMessage = errorData
        } else if (errorData?.message) {
          errorMessage = errorData.message
        } else if (typeof errorData === 'object') {
          errorMessage = errorData.message || t('common.serverError', 'حدث خطأ في الخادم')
        }
      } else if (error?.message) {
        errorMessage = error.message
      }
      
      setPaymentError(errorMessage)
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="donation-modal-title"
      >
        <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto relative">
          <button
            onClick={onClose}
            className="absolute top-4 left-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label={t('common.close', 'إغلاق')}
          >
            <X className="w-5 h-5" />
          </button>

          <div className="pr-10">
            <h2
              id="donation-modal-title"
              className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2"
            >
              {t('donations.donateTo', 'التبرع لمؤسسة')} {charity.name}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {t('common.registry', 'السجل')}: {charity.registry}
            </p>
          </div>

          {step === 'details' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.donationAmount', 'مبلغ التبرع')} ({t('common.currency', 'ج.م')})</label>
                <Input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.name', 'الاسم')}</label>
                <Input
                  type="text"
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  placeholder={t('common.fullName', 'اسمك الكامل')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{t('common.email', 'البريد الإلكتروني')}</label>
                <Input
                  type="email"
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  placeholder="example@email.com"
                />
              </div>

              <div className="pt-4">
                <Button
                  variant="primary"
                  className="w-full"
                  onClick={handleNext}
                  disabled={!isValid}
                >
                  {t('common.continueToPayment', 'المتابعة للدفع')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4 border border-primary-200 dark:border-primary-800">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-700 dark:text-gray-300">{t('common.amount', 'المبلغ')}</span>
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {donationAmount.toLocaleString(i18n.language === 'ar' ? 'ar-EG' : 'en-US')} {t('common.currency', 'ج.م')}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t('common.donor', 'المتبرع')}</span>
                  <span className="text-gray-900 dark:text-gray-100">{donorName}</span>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">
                  {t('donations.paymentMethod', 'اختر طريقة الدفع')}
                </h3>
                <div className="space-y-2">
                  <button className="w-full p-4 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-500 transition-colors text-right ltr:text-left">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {t('donations.cardPayment', 'بطاقة ائتمانية/خصم')}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Visa, Mastercard, Meeza
                        </p>
                      </div>
                      <CreditCard className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                <Lock className="w-4 h-4" />
                <span>{t('donations.securePayment', 'الدفع آمن ومشفر')}</span>
              </div>

              {paymentError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-400">
                  {paymentError}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={() => setStep('details')}
                  disabled={isProcessing}
                >
                  {t('common.previous', 'رجوع')}
                </Button>
                <Button
                  variant="primary"
                  className="flex-1"
                  onClick={handleConfirm}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 rtl:mr-2 ltr:ml-2 animate-spin inline" />
                      {t('common.processing', 'جاري المعالجة...')}
                    </>
                  ) : (
                    t('donations.confirmDonation', 'تأكيد التبرع')
                  )}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  )
}
